import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  increment,
} from "firebase/firestore";
import { db } from "../../firebase/config";

function formatRelativeTime(timestamp) {
  if (!timestamp) return "Just now";

  const now = new Date();
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return "Just now";
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return `${mins}m ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h ago`;
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago`;
  }

  return date.toLocaleDateString();
}

function CommentSection({ postId, user }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [commentAuthorName, setCommentAuthorName] = useState(() => {
    if (user?.isGuest || !user) return "";
    return user?.displayName || user?.email?.split("@")[0] || "";
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const commentsRef = collection(
      db,
      "messageBoard",
      "data",
      "posts",
      postId,
      "comments"
    );
    const q = query(commentsRef, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const commentsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setComments(commentsData);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching comments:", err);
        setError("Failed to load comments");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [postId]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !commentAuthorName.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      // Add the comment
      const commentsRef = collection(
        db,
        "messageBoard",
        "data",
        "posts",
        postId,
        "comments"
      );
      await addDoc(commentsRef, {
        content: newComment.trim(),
        authorId: user?.uid || "guest",
        authorName: commentAuthorName.trim(),
        authorIsGuest: user?.isGuest || !user,
        createdAt: serverTimestamp(),
      });

      // Update comment count on the post
      const postRef = doc(db, "messageBoard", "data", "posts", postId);
      await updateDoc(postRef, {
        commentCount: increment(1),
      });

      setNewComment("");
    } catch (err) {
      console.error("Error adding comment:", err);
      setError("Failed to add comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="comment-section">
      <h3 className="comments-header">
        Comments ({comments.length})
      </h3>

      <form className="comment-form" onSubmit={handleSubmitComment}>
        <input
          type="text"
          placeholder="Your name..."
          value={commentAuthorName}
          onChange={(e) => setCommentAuthorName(e.target.value)}
          maxLength={50}
          required
        />
        <textarea
          placeholder="Write a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          rows={3}
        />
        {error && <p className="form-error">{error}</p>}
        <button
          type="submit"
          className="submit-comment-btn"
          disabled={submitting || !commentAuthorName.trim() || !newComment.trim()}
        >
          {submitting ? "Posting..." : "Post Comment"}
        </button>
      </form>

      <div className="comments-list">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner-small"></div>
            <p>Loading comments...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="empty-comments">
            <p>No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="comment-item">
              <div className="comment-header">
                <span className="comment-author">
                  <span className="author-avatar-small">
                    {comment.authorIsGuest ? "👤" : "👨‍💻"}
                  </span>
                  {comment.authorIsGuest && <span className="guest-badge-small">Guest</span>}
                  {comment.authorName}
                </span>
                <span className="comment-time">
                  {formatRelativeTime(comment.createdAt)}
                </span>
              </div>
              <p className="comment-content">{comment.content}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default CommentSection;
